import { useCallback, useEffect, useState, type MouseEventHandler, type ReactNode } from "react";

import clsx from "clsx";
import Head from "next/head";
import { useRouter } from "next/router";

import { fireWebsiteLeadEvent } from "../lib/google-ads";
import usePage from "./layout/page/use-page";
import Modal from "./modal";

type DiscoverEnerfloProps = {
  buttonText?: string;
  buttonClassName?: string;
  children?: (props: { onOpen: MouseEventHandler<HTMLButtonElement> }) => ReactNode;
  meetingSrc?: string;
};

const defaultButtonClasses = "w-full xs:w-auto md:w-full text-sm sm:text-base";

const getEmbeddedSrc = (src: string) => {
  const url = new URL(src);
  url.searchParams.set("embed", "true");
  return url.toString();
};

const DiscoverEnerfloButtonWithOverlay = ({
  buttonText = "Book a Discovery Call",
  buttonClassName = defaultButtonClasses,
  children,
  meetingSrc = "https://meetings-na2.hubspot.com/mrrhino/discovery-call",
}: DiscoverEnerfloProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const router = useRouter();
  const pageDetails = usePage();
  const [ready, setReady] = useState(false);

  const onOpen = () => setIsOpen(true);
  const onClose = () => setIsOpen(false);

  const open: MouseEventHandler<HTMLButtonElement> = useCallback(
    e => {
      if (window["analytics"] !== undefined && process.env.NODE_ENV === "production") {
        const idPath = `${router.asPath !== "/" ? router.asPath : ""}/#${(e.target as HTMLElement).id}`;

        window["analytics"].page({
          title: `${pageDetails.title} | Discovery Call Request`,
          url: `${process.env.SITE_URL}${idPath}`,
          path: idPath,
          referrer: "https://enerflo.com",
        });
      }

      onOpen();
    },
    [pageDetails.title, router.asPath]
  );

  useEffect(() => {
    setReady(true);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== "https://meetings.hubspot.com") {
        return;
      }

      if (event.data?.meetingBookSucceeded === true) {
        fireWebsiteLeadEvent("Book a Discovery Call");
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [isOpen]);

  return (
    <>
      <Head>
        {ready && isOpen && (
          <script
            async
            type="text/javascript"
            src="https://static.hsappstatic.net/MeetingsEmbed/ex/MeetingsEmbedCode.js"
          ></script>
        )}
      </Head>
      <Modal isOpen={isOpen} onClose={onClose} contentClassName="sm:max-w-[95vw] md:max-w-2xl">
        <div className="meetings-iframe-container" data-src={getEmbeddedSrc(meetingSrc)} />
      </Modal>
      {children?.({ onOpen: open }) || (
        <button
          className={clsx("btn btn-primary uppercase font-bold whitespace-nowrap", buttonClassName)}
          onClick={open}
          aria-label={buttonText}
          id="discover-enerflo-button"
        >
          {buttonText}
        </button>
      )}
    </>
  );
};

export default DiscoverEnerfloButtonWithOverlay;
